'use client';

import { type FormEvent, useState } from 'react';

import Link from 'next/link';

type State = 'idle' | 'sending' | 'sent' | 'error';

/** End-of-article signup. Light on purpose: no animation library in the article bundle. */
export function PostSubscribe() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<State>('idle');
  const [message, setMessage] = useState('');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setState('sending');
    try {
      const res = await fetch('/api/addSubscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error);
      setState('sent');
      setMessage(data.message);
    } catch (error) {
      setState('error');
      setMessage(
        error instanceof Error && error.message
          ? error.message
          : 'Something went wrong. Please try again.'
      );
    }
  }

  return (
    <aside
      aria-labelledby='post-subscribe-title'
      className='border-border bg-card my-12 rounded-2xl border p-6 sm:p-8'>
      <h2
        id='post-subscribe-title'
        className='text-foreground mb-2 font-serif text-2xl font-bold'>
        Get the next analysis by email
      </h2>
      <p className='text-muted-foreground mb-5 text-sm leading-relaxed'>
        A short weekly email with the new posts and their charts. Unsubscribe
        with one click.
      </p>

      {state === 'sent' ? (
        <p
          role='status'
          className='text-foreground text-sm font-medium'>
          {message}
        </p>
      ) : (
        <form
          onSubmit={onSubmit}
          className='flex flex-col gap-3 sm:flex-row'>
          <label
            htmlFor='post-subscribe-email'
            className='sr-only'>
            Email address
          </label>
          <input
            id='post-subscribe-email'
            type='email'
            required
            autoComplete='email'
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder='you@company.com'
            className='border-border bg-background text-foreground min-w-0 flex-1 rounded-full border px-4 py-2.5 text-sm'
          />
          <button
            type='submit'
            disabled={state === 'sending'}
            className='rounded-full bg-[#0E3D2D] px-6 py-2.5 text-sm font-bold text-white hover:opacity-90 disabled:opacity-60'>
            {state === 'sending' ? 'Sending…' : 'Subscribe'}
          </button>
        </form>
      )}
      {state === 'error' && (
        <p
          role='alert'
          className='text-destructive mt-3 text-sm'>
          {message}
        </p>
      )}
      <p className='text-muted-foreground mt-4 text-xs'>
        By subscribing you agree to our{' '}
        <Link
          href='/privacy'
          className='underline'>
          privacy policy
        </Link>
        .
      </p>
    </aside>
  );
}
